import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { AddShift, addShift, dateToISO } from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface AddNewShiftToSinglePersonnelModalProps extends ModalProps {
  personelId: string;
  title?: string;
  companyId: string;
  locationId: string;
  selectedDate: Date;
}

export function AddNewShiftModal({
  personelId,
  title,
  companyId,
  selectedDate,
  locationId,
  onClose,
  ...props
}: AddNewShiftToSinglePersonnelModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [beginningTime, setBeginningTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [calculatedTime, setCalculatedTime] = useState("");
  const { mutate, isPending } = useMutation({
    mutationFn: (shift: AddShift) => addShift(shift),
    onSuccess: () => {
      toast.success(t("shift.Shifts have been planned"));
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      onClose?.();
    },
    onError: () => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const handleSubmit = () => {
    const workStartDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );
    const workEndDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );

    const [startHour, startMinute] = beginningTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    workStartDate.setHours(startHour, startMinute, 0, 0);
    workEndDate.setHours(endHour, endMinute, 0, 0);

    const payLoad = {
      plannedStartDate: dateToISO(workStartDate),
      plannedEndDate: dateToISO(workEndDate),
      companyId: companyId,
      companyLocationId: locationId,
      userId: personelId,
    } as AddShift;

    mutate(payLoad);
  };

  const handleClose = useCallback(() => {
    onClose?.();
    setBeginningTime("");
    setEndTime("");
    setCalculatedTime("");
  }, [onClose]);

  const calculateTimeDifference = (start: string, end: string) => {
    const startTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );
    const endTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );

    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);

    startTime.setHours(startHour, startMinute);
    endTime.setHours(endHour, endMinute);

    const diffMs = endTime.getTime() - startTime.getTime();
    if (diffMs < 0) return t("shift.Invalid time range");

    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHrs.toString().padStart(2, "0")}:${diffMins.toString().padStart(2, "0")}`;
  };

  const handleTimeChange = (start: string, end: string) => {
    if (start && end) {
      const calculated = calculateTimeDifference(start, end);
      setCalculatedTime(calculated);
    }
  };

  return (
    <Modal
      {...props}
      onClose={handleClose}
      title={title}
      actionButtons={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t("common.Discard")}
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isPending}>
            {t("shift.Plan Shifts")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col w-100 gap-5">
        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Shift Beginning and End")}
          </label>
          <div className="flex flex-row w-full gap-2.5 w-100">
            <Input
              name="beginningTime"
              placeholder={t("shift.Beginning")}
              className="w-[50%]"
              type="time"
              value={beginningTime}
              onChange={(e) => {
                setBeginningTime(e.target.value);
                handleTimeChange(e.target.value, endTime);
              }}
            />
            <Input
              type="time"
              name="endTime"
              placeholder={t("shift.End")}
              className="w-[50%]"
              value={endTime}
              onChange={(e) => {
                setEndTime(e.target.value);
                handleTimeChange(beginningTime, e.target.value);
              }}
            />
          </div>
        </div>
        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Calculated Time")}
          </label>
          <div className="flex flex-col w-full">
            <Input
              placeholder={calculatedTime || t("shift.Time will be calculated")}
              readOnly
              disabled
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
